import { dateParamSignal } from 'CalendarFilter/DatePicker';
import Box from 'components/Box';
import { IDatePickerValue } from 'components/DatePicker';
import { IModalHeaderProps } from 'components/Modal';
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { ICustomerUI } from 'types/customer';
import { IStaffUI } from 'types/staff';
import AddCustomer from 'widgets/AddCustomer';
import { StaffAvailablePickerUI, useStaffAvailablePickerRef } from 'widgets/Staff/StaffAvailablePicker';
import { TimeSlotAvailablePickerUI, useTimeSlotAvailablePickerRef } from 'widgets/Staff/TimeSlotAvailablePicker';
import CustomerSection from './CustomerSection';
import StaffSection from './StaffSection';
import Text from 'components/Text';
import { on } from 'events';
import { Row } from 'components/Box/Row';


type QuickBookMode = 'caller' | 'book';
type IQuickBookContextProps = {};
export type IQuickBookContextCallerResult = {
  callerId: string;
  customer: ICustomerUI;
  staff: IStaffUI;
  timeSlot: IDatePickerValue;
};
type IQuickBookContextRef = {
  caller: (callerId: string, _customer?: ICustomerUI | undefined) => Promise<IQuickBookContextCallerResult | null>;
};
export const QuickBookContextRef = React.createRef<IQuickBookContextRef>();
const QuickBookContextContext = forwardRef<IQuickBookContextRef, IQuickBookContextProps>((props, ref) => {
  const staffPickerRef = useStaffAvailablePickerRef();
  const editStaffPickerRef = useStaffAvailablePickerRef();
  const timeSlotPickerRef = useTimeSlotAvailablePickerRef();
  const [currentCustomer, setCurrentCustomer] = useState<ICustomerUI | undefined>(undefined);
  const [currentStaff, setCurrentStaff] = useState<IStaffUI | undefined>(undefined);
  const callbackCallerResolve = useRef<(result: IQuickBookContextCallerResult | null) => void>(() => undefined);
  const [callerId, setCallerId] = useState<string | undefined>(undefined);

  const [mode, setMode] = useState<QuickBookMode | undefined>(undefined);

  const handleCaller: IQuickBookContextRef['caller'] = async (_callerId, _customer) => {
    setCurrentCustomer(undefined);
    setCurrentStaff(undefined);
    setCallerId(undefined);
    setMode(undefined);
    if (!_customer) return new Promise<IQuickBookContextCallerResult | null>((resolve) => {
      resolve(null);
    });
    const customer = !_customer?.id ? await AddCustomer.add({ phoneNumber: _customer?.phone }) : _customer;
    setCurrentCustomer(customer);
    setCallerId(_callerId);
    const _staff = await staffPickerRef.current?.get();
    setCurrentStaff(_staff);
    setMode('caller');
    timeSlotPickerRef.current?.open(_staff?.id || '', dateParamSignal.value);
    return new Promise((resolve) => {
      callbackCallerResolve.current = resolve;
    });
  };

  const onChooseTimeSlot = (timeSlot: IDatePickerValue) => {
    timeSlotPickerRef.current?.close();
    switch (mode) {
      case 'caller': {
        if (!callerId || !currentCustomer || !currentStaff) return;
        callbackCallerResolve.current?.({
          callerId: callerId || '',
          customer: currentCustomer,
          staff: currentStaff,
          timeSlot: timeSlot,
        });
        break;
      }
      case 'book':
        break;
    }
  };

  const handleEditStaff = async () => {
    const _staff = await editStaffPickerRef.current?.get();
    setCurrentStaff(_staff);
    await timeSlotPickerRef.current?.fetchData(_staff?.id || '');
  };

  const handleChangeCustomer = (customer?: ICustomerUI | null) => {
    setCurrentCustomer(customer ?? undefined);
  };
  
  const QuickBookTitle = () => {

    return (
      <Box width='100%' display='flex' alignItems='center' flexDirection='column' p='4' style={{paddingBottom: 0}}>
        <StaffSection currentStaff={currentStaff} onEditStaff={handleEditStaff} />
        <Box width={'100%'}>
          <Text textAlign='left' variant='LABEL_SECTION' style={{textTransform: 'uppercase'}}>Customer:</Text>
          <CustomerSection currentCustomer={currentCustomer} handleChangeCustomer={handleChangeCustomer} />
        </Box>
      </Box>
    );
  };  

  const renderHeader = ({ onCancel }: IModalHeaderProps) => {
    return (
      <Header>
        <span>History Caller ID</span>
        <button style={{ right: 16, position: 'absolute', bottom: 0, top: 0 }} onClick={onCancel}><p className="text-action">CLOSE</p></button>
      </Header>
    );
  };

  const renderFooter = ({ onCancel }: { onCancel: IModalHeaderProps['onCancel'] }) => {
    return (
      <Footer>
        <button className='btn btn-delete' onClick={onCancel}>
          <Text className="label">ClOSE</Text>
        </button>
      </Footer>
    );
  };

  useImperativeHandle(ref, () => ({
    caller: handleCaller,
  }));

  return (
    <>
      <StaffAvailablePickerUI ref={staffPickerRef} zIndex={80} />
      <StaffAvailablePickerUI title='Change Staff' ref={editStaffPickerRef} zIndex={81} />
      <TimeSlotAvailablePickerUI 
        newTitle
        renderHeader={renderHeader}
        ref={timeSlotPickerRef} 
        zIndex={80} 
        onChoose={onChooseTimeSlot}
        renderTitle={<QuickBookTitle/>}
        renderFooter={renderFooter}
      />
    </>
  );
});

QuickBookContextContext.displayName = 'QuickBookContextContext';

const QuickBookContext = {
  Portal: () => <QuickBookContextContext ref={QuickBookContextRef} />,
  caller: ((callerId: string, _customer?: ICustomerUI | undefined) => {
    return QuickBookContextRef.current?.caller(callerId, _customer);
  }) as IQuickBookContextRef['caller'],
};

export default QuickBookContext;

const Header = styled.div`
  display: flex;
  height: 48px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border-bottom: 1px solid #148CF1;
  background: #E8F3FF;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
  position: relative;
  span {
    align-self: stretch;
    color: #1D2129;
    text-align: center;
    font-family: Poppins;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
  }
  .text-action {
    color: #F05326;
    font-family: Poppins;
    font-size: 22px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 133.333% */
    white-space: nowrap;
  }
`;

const Footer = styled(Row)`
  position: sticky;
  padding: 0.75rem 1.5rem;
  margin-top: 1rem;
  gap: 1rem;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: #fff;
  .btn {
    display: flex;
    height: 48px;
    padding: 10px 16px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    border-radius: 8px;

    .label {
      text-align: center;

      /* Body 1 */
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px; /* 150% */
    }
    
  }
  .btn-delete, .btn-close {
    border-radius: 2px;
    border: 1px solid #7B7B7B;
    background-color: #7B7B7B;
    .label {
      color: #fff;
    }
  }

  .btn-update {
    border-radius: 2px;
    background: #232F3E;
    .label {
      color: #FFF;
    }

    &:disabled {
      opacity: 0.7;
    }
  }
  .btn-confirm {
    border-radius: 2px;
    background: #0085E7;
    .label {
      color: #FFF;
    }

    &:disabled {
      opacity: 0.7;
    }
  }
`;
